import React, { useEffect, useMemo, useRef, useState } from "react";
import { withRouter, Prompt } from "react-router-dom";
import useStore from "../../../context/useStore";
import { financierActions } from "../../../context/actions/financierActions";
import { catalogStoreActions } from "../../../context/actions/catalogStoreActions";
import Loader from "../../common/Loader";
import ConfirmModal from "../../common/ConfirmModal/ConfirmModal";
import PaymentTable from "../Finance/PaymentTable";
import useFinanceDetail, { financierListAtom } from "./hooks/useFinanceDetail";
import financeV2Adapters from "../../../adapters/financeV2Adapters";
import DevisSearchV2 from "./components/DevisSearchV2";
import FinanceTabV2 from "./components/FinanceTabV2";
import TauxVrManuelModal from "./components/TauxVrManuelModal";
import EnvoyerAuFinanceurModal from "./components/EnvoyerAuFinanceurModal";
import FuelBadge from "./components/FuelBadge";
import EnveloppeSelector from "./components/EnveloppeSelector";
import AccordionSection from "./components/AccordionSection";
import DocumentsUploader from "./components/DocumentsUploader";
import NumeroContratFinanceur from "./components/NumeroContratFinanceur";
import CompteComptable from "./components/CompteComptable";
import SoldesSection from "./components/SoldesSection";
import { TYPE_TABLEAU_AMORTISSEMENT_SOURCE } from "./components/EcheancierUploader";
import AccordFinancementSection from "./components/AccordFinancementSection";
import styles from "./FinanceDetailV2.module.scss";
import { useAtom } from "jotai";
import { pdf } from "@react-pdf/renderer";
import { toast } from "react-toastify";
import financeApiService from "../../../services/financeApiService";
import PaymentTableDocument from "../Finance/PaymentTableDocument";
import { buildRecapRows, buildRecapFileName } from "../Finance/recapData";
import useTVA from "../../../hooks/useInit/useTVA";
import tools from "../../../helpers/tools";
import {
  estGestionnaire,
  peutSaisirEcheancier,
} from "../../../helpers/echeancierTools";

const STATUS_BADGE_CLASSES = {
  1: styles.statusDraft,
  2: styles.statusValidated,
  3: styles.statusConfirmed,
  4: styles.statusLocked,
  5: styles.statusTransmitted,
  6: styles.statusSigned,
  // LS-629 — statut terminal alternatif, hors du fil de vie séquentiel (LIFECYCLE_STEPS).
  7: styles.statusAbandoned,
};

const LIFECYCLE_STEPS = [
  { status: 1, label: "Brouillon" },
  { status: 2, label: "Demande envoyée" },
  { status: 3, label: "Accord reçu" },
  { status: 4, label: "Dossier complet" },
  { status: 5, label: "Dossier transmis" },
  { status: 6, label: "Transmis et signé" },
];

const FinanceDetailV2 = (props) => {
  const [state, dispatch] = useStore();
  const financier_actions = financierActions(state, dispatch, props);
  const catalog_store_actions = catalogStoreActions(state, dispatch, props);
  const [, setFinancierList] = useAtom(financierListAtom);

  // Mode embedded : rendu dans FinanceModalV2 par-dessus le devis,
  // sans dépendre de l'URL ni rediriger.
  const embedded = !!props.embedded;

  const {
    financeData,
    isDirty,
    savedPeriodsCount,
    isLoading,
    isCalculating,
    quotationData,
    demandeQuotations,
    isSearchingQuotations,
    searchValidQuotations,
    loadDemandeQuotations,
    selectedTab,
    setSelectedTab,

    selectQuotation,
    unselectQuotation,
    updatePeriodField,
    commitPeriodField,
    addPeriod,
    removePeriod,
    changeEnveloppe,
    saisirMensualiteRetenue,

    loadDemande,
    loadDemandeFromDevis,
    saveDemande,
    envoyerDemande,
    envoyerAuFinanceur,
    accordRecuDemande,
    dossierCompletDemande,
    dossierTransmisDemande,
    transmisEtSigneDemande,
    abandonnerDemande,
    basculerTodo,
    confirmState,
    confirmTransition,
    cancelTransition,
    resetFinance,
    tauxVrManuelPrompt,
    submitTauxVrManuel,
    cancelTauxVrManuel,
  } = useFinanceDetail();

  const financierData = state.financier.items;
  const constantData = state.constant.items;
  const { getTVAByCatalog } = useTVA();
  const isNew = embedded
    ? !financeData.uuid
    : props.match.params.uuid === "create";
  // LS-607 — les gestionnaires (Admin/Super-admin) ne sont jamais verrouillés,
  // quel que soit le statut atteint par la demande.
  const isGestionnaire = estGestionnaire(state.auth.user.role);
  // LS-637 — les commerciaux restent modifiables tant que Dossier complet (4)
  // n'est pas atteint ; verrouillés dès Dossier complet inclus.
  // LS-629 — Abandonnée (7) est le seul statut verrouillé pour tout le monde, y compris
  // les gestionnaires (contrairement aux autres statuts, jamais verrouillés pour eux).
  const isAbandonnee = financeData.status === 7;
  const isReadOnly =
    isAbandonnee || (!isGestionnaire && financeData.status >= 4);
  // LS-612 — saisie de l'échéancier réel possible dès Accord reçu (3),
  // indépendamment du verrouillage classique ci-dessus.
  // LS-629 — sauf demande abandonnée, jamais ressaisissable.
  // LS-724 — réservée aux gestionnaires (règle dans echeancierTools).
  const canSaisirEcheancier = peutSaisirEcheancier(
    financeData.status,
    state.auth.user.role,
  );
  // LS-716 — le numéro de contrat financeur garde son propre seuil : dès Accord
  // reçu, tout profil (hors scope LS-724), sauf abandonnée.
  const canSaisirNumeroContrat = financeData.status >= 3 && !isAbandonnee;

  const preselectConsumedRef = useRef(false);
  // Vrai pendant la recherche d'une demande déjà liée au devis : évite
  // d'afficher brièvement le formulaire de création vide avant le résultat.
  const [isCheckingExisting, setIsCheckingExisting] = useState(false);

  // Étapes dépliées dans la checklist. L'étape courante est ajoutée
  // automatiquement au chargement de la demande et à chaque transition ;
  // l'utilisateur reste libre de déplier/replier les autres (historique).
  const [expandedSteps, setExpandedSteps] = useState(new Set());
  // ST1-2069 — les documents rattachés à un devis (CPI, calendrier des loyers)
  // sont listés à deux endroits (section "Documents" et onglet du devis) :
  // chaque compteur force le rechargement de l'autre liste après un dépôt ou
  // une suppression, sans recharger celle qui vient d'agir.
  const [documentsVersion, setDocumentsVersion] = useState(0);
  const [devisDocumentsVersion, setDevisDocumentsVersion] = useState(0);
  // LS-625 — modale d'envoi (ou renvoi) par email au financeur, indépendante
  // des transitions de statut séquentielles (TRANSITIONS ci-dessous).
  const [isEnvoyerAuFinanceurOpen, setIsEnvoyerAuFinanceurOpen] =
    useState(false);
  const lastAutoExpandRef = useRef({ uuid: null, status: null });
  useEffect(() => {
    if (!financeData.uuid) return;
    const last = lastAutoExpandRef.current;
    if (last.uuid !== financeData.uuid || last.status !== financeData.status) {
      lastAutoExpandRef.current = {
        uuid: financeData.uuid,
        status: financeData.status,
      };
      setExpandedSteps((prev) => new Set(prev).add(financeData.status));
    }
  }, [financeData.uuid, financeData.status]);
  const toggleStep = (status) =>
    setExpandedSteps((prev) => {
      const next = new Set(prev);
      if (next.has(status)) next.delete(status);
      else next.add(status);
      return next;
    });

  useEffect(() => {
    financier_actions.getAllFinanciers();

    const routeUuid = embedded ? null : props.match.params.uuid;
    const isCreate = embedded || routeUuid === "create";

    if (!isCreate) {
      loadDemande(routeUuid);
    } else {
      // Lien rapide ou modale depuis une ligne de devis validé.
      const params = embedded
        ? null
        : new URLSearchParams(props.location.search);
      const devisUuid = embedded ? props.devisUuid : params.get("devis");
      const clientUuid = embedded ? props.clientUuid : params.get("client");

      if (devisUuid && !preselectConsumedRef.current) {
        preselectConsumedRef.current = true;
        setIsCheckingExisting(true);
        (async () => {
          try {
            // Une demande liée au devis existe-t-elle déjà ?
            // (route API à venir : 404 / route absente => mode création)
            const existingUuid = await loadDemandeFromDevis(devisUuid);
            if (existingUuid) {
              if (!embedded && props.history) {
                props.history.replace("/financements/" + existingUuid);
              }
              return;
            }
            selectQuotation({ uuid: devisUuid });
            searchValidQuotations(
              clientUuid
                ? { client: clientUuid }
                : { quotationDetails: [devisUuid] }
            );
          } finally {
            setIsCheckingExisting(false);
          }
        })();
      }
    }
    return () => resetFinance();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (financierData.length > 0) {
      setFinancierList(financierData);
    }
  }, [financierData, setFinancierList]);

  // Enveloppes par financeur (cumul financeur + consommation par enveloppe)
  // pour le sélecteur capacity-aware et les KPI.
  const [groupes, setGroupes] = useState({});
  const financeursKey = financierData.map((f) => f.uuid).join(",");
  useEffect(() => {
    if (financierData.length === 0) return undefined;
    let cancelled = false;
    Promise.all(
      financierData.map((f) =>
        financeApiService
          .listerEnveloppes(f.uuid)
          .then((res) => ({
            f,
            data: financeV2Adapters.fromApi.enveloppesList(res.data),
          }))
          .catch(() => null),
      ),
    ).then((results) => {
      if (cancelled) return;
      const map = {};
      results.filter(Boolean).forEach(({ f, data }) => {
        map[f.uuid] = {
          financeurNom: f.name,
          consommation: data.consommation,
          enveloppes: data.enveloppes,
        };
      });
      setGroupes(map);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [financeursKey]);

  // Index plat enveloppe → { …enveloppe, financeurNom, financeurUuid }.
  const enveloppeIndex = useMemo(() => {
    const idx = {};
    Object.entries(groupes).forEach(([finUuid, g]) =>
      (g.enveloppes || []).forEach((e) => {
        idx[e.uuid] = {
          ...e,
          financeurNom: g.financeurNom,
          financeurUuid: finUuid,
        };
      }),
    );
    return idx;
  }, [groupes]);
  const selectedEnveloppe = enveloppeIndex[financeData.enveloppe] || null;

  // Date de fin la plus tardive parmi les dernières périodes de tous les
  // devis de la demande — sert à signaler une enveloppe dont la date_maximale
  // serait dépassée, avant même toute tentative d'enregistrement.
  const derniereEcheance = useMemo(() => {
    const dates = financeData.quotations
      .map((q) => q.periods?.[q.periods.length - 1]?.dEnd)
      .filter(Boolean);
    return dates.length > 0 ? dates.reduce((max, d) => (d > max ? d : max)) : null;
  }, [financeData.quotations]);

  const loadedDemandeUuidRef = useRef(null);
  useEffect(() => {
    if (
      !isNew &&
      financeData?.uuid &&
      financeData.quotations.length > 0 &&
      loadedDemandeUuidRef.current !== financeData.uuid
    ) {
      loadedDemandeUuidRef.current = financeData.uuid;
      loadDemandeQuotations(financeData.uuid);
    }
  }, [
    isNew,
    financeData.uuid,
    financeData.quotations.length,
    loadDemandeQuotations,
  ]);

  // Filet de sécurité fermeture/rafraîchissement navigateur sur état non enregistré.
  useEffect(() => {
    if (!isDirty) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  function onSearchValidQuotations(search = {}) {
    // Recherche = pool de devis disponibles (sans demandeUuid).
    searchValidQuotations(search);
  }

  function getQuotationObj(uuid) {
    // Résout d'abord parmi les devis de la demande, puis dans le pool de
    // recherche (pour les devis fraîchement ajoutés depuis la recherche).
    const sources = [...demandeQuotations, ...quotationData];
    for (const qs of sources) {
      const found = qs.quotations.find((q) => q.uuid === uuid);
      if (found) return found;
    }
    return null;
  }

  // LS-637 — un commercial n'a pas forcément, dans son périmètre UserGroup, le
  // catalogue de chaque véhicule financé (nécessaire au calcul de la TVA). Complète
  // le catalogStore avec les catalogues des devis de la demande courante, uniquement
  // dans ce contexte (pas de recherche catalogue générale ouverte pour autant).
  useEffect(() => {
    const catalogUuids = financeData.quotations
      .map((q) => getQuotationObj(q.quotation)?.vehicle?.catalog?.uuid)
      .filter(Boolean);
    if (catalogUuids.length > 0) {
      catalog_store_actions.chargerCataloguesManquants(catalogUuids);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [financeData.quotations, demandeQuotations, quotationData]);

  // Génère le récap PDF localement et l'uploade sur la demande. Non bloquant :
  // un échec n'empêche pas l'enregistrement/validation/confirmation.
  //
  // LS-672 — sauf `force` (envoi au financeur, cf. handleEnvoyerAuFinanceur),
  // ne régénère/ré-uploade que si la demande a changé depuis la dernière
  // génération : lit l'état serveur à jour (plutôt que `financeData`, dont la
  // fraîcheur dépend du cycle de rendu React) pour comparer sa date de
  // dernière modification à celle du dernier tableau généré.
  async function uploadRecap(uuid, { force = false } = {}) {
    if (!uuid) return;
    try {
      if (!force) {
        const [demandeRes, recapRes] = await Promise.all([
          financeApiService.getDemande(uuid),
          financeApiService.getRecap(uuid).catch(() => null),
        ]);
        if (
          financeV2Adapters.tableauRecapitulatifAJour(
            demandeRes.data.updated_at,
            recapRes?.data?.createdAt,
          )
        ) {
          return;
        }
      }
      const rows = buildRecapRows({
        finances: financeData,
        quotationSources: [...demandeQuotations, ...quotationData],
        constantData,
        getTVAByCatalog,
      });
      if (rows.length === 0) return;
      const fileName = buildRecapFileName(financeData.reference || uuid);
      const blob = await pdf(
        <PaymentTableDocument rows={rows} title={fileName} />,
      ).toBlob();
      const file = new File([blob], fileName, { type: "application/pdf" });
      await financeApiService.uploaderRecap(uuid, file);
    } catch (err) {
      console.error("Erreur upload récap auto", err);
      toast.warning(
        "Le tableau récapitulatif n'a pas pu être enregistré automatiquement.",
      );
    }
  }

  async function handleSave() {
    const uuid = await saveDemande();
    if (!uuid) return;
    await uploadRecap(uuid);
    if (isNew && !embedded && props.history) {
      props.history.push("/financements/" + uuid);
    }
  }

  // Transition disponible pour le statut courant (aucune pour 6, terminal).
  const TRANSITIONS = {
    1: { label: "Marquer comme envoyée", icon: "paper-plane", action: envoyerDemande },
    2: { label: "Marquer accord reçu", icon: "check", action: accordRecuDemande },
    3: {
      label: "Marquer dossier complet",
      icon: "folder",
      action: dossierCompletDemande,
    },
    4: {
      label: "Marquer dossier transmis",
      icon: "share",
      action: dossierTransmisDemande,
    },
    5: {
      label: "Marquer transmis et signé",
      icon: "handshake",
      action: transmisEtSigneDemande,
    },
  };
  const nextTransition = TRANSITIONS[financeData.status];

  // LS-625 — le libellé et le titre de la modale distinguent premier envoi et
  // renvoi ; le petit encart de la section Tableau récapitulatif rappelle le
  // dernier destinataire pour éviter un renvoi par inadvertance à la mauvaise
  // adresse.
  const dejaEnvoyeAuFinanceur = !!financeData.dernierEnvoiFinanceur;
  const envoyerAuFinanceurLabel = dejaEnvoyeAuFinanceur
    ? "Renvoyer au financeur"
    : "Envoyer au financeur";

  // Todos configurés pour l'étape cible de la transition — affichés dans la
  // popup de confirmation. Progression strictement séquentielle (+1).
  const nextStepTodoLabels = (selectedEnveloppe?.todos || [])
    .filter((t) => t.statut === financeData.status + 1)
    .sort((a, b) => a.ordre - b.ordre)
    .map((t) => t.libelle);

  async function handleTransition() {
    const uuid = await nextTransition.action(nextStepTodoLabels);
    if (uuid) await uploadRecap(uuid);
  }

  // LS-629 — pas de récap à régénérer : les devis rattachés viennent d'être détachés.
  async function handleAbandonner() {
    await abandonnerDemande();
  }

  // LS-625 — le récap imprimé (bouton "Imprimer le tableau") n'est qu'un aperçu
  // client, jamais uploadé : sans ceci, la pièce jointe envoyée au financeur
  // pourrait être une version obsolète (dernier enregistrement/transition),
  // pas l'état actuellement affiché dans la modale. Régénère et réuploade
  // AVANT l'envoi (contrairement à handleTransition, où c'est après) pour que
  // le contrôle bloquant côté API (média manquant) porte sur la version courante.
  async function handleEnvoyerAuFinanceur(payload) {
    await uploadRecap(financeData.uuid, { force: true });
    return envoyerAuFinanceur(payload);
  }

  // `financierData` peut légitimement rester vide une fois le chargement
  // terminé (ex: aucun financeur remonté pour ce compte) : ne pas bloquer
  // indéfiniment sur le loader dans ce cas, sous peine d'écran figé pour les
  // super administrateurs. `EnveloppeSelector` gère déjà l'affichage vide.
  if (
    (state.financier.isLoading && financierData.length === 0) ||
    isCheckingExisting
  )
    return <Loader />;

  const statusLabel =
    financeV2Adapters.STATUS_LABELS[financeData.status] || "Inconnu";

  // KPI: capacité disponible de l'enveloppe sélectionnée.
  const envConso = selectedEnveloppe ? selectedEnveloppe.consommation : null;
  const enveloppeOrigine = envConso ? envConso.enveloppeOrigine : 0;
  const enveloppeDispo = envConso ? envConso.disponible : 0;
  const enveloppeConsomme = envConso
    ? envConso.reserve + envConso.engage
    : 0;
  const enveloppeConsoRatio = financeV2Adapters.enveloppeRatio(
    enveloppeConsomme,
    enveloppeOrigine,
  );
  const enveloppeDispoRatio = financeV2Adapters.enveloppeRatio(
    enveloppeDispo,
    enveloppeOrigine,
  );
  const enveloppeColor = financeV2Adapters.enveloppeColor(enveloppeConsoRatio);
  const enveloppeColorText =
    financeV2Adapters.enveloppeColorText(enveloppeConsoRatio);

  // KPI: nom du financeur (déduit de l'enveloppe sélectionnée, repli sur la
  // valeur conservée pour l'affichage d'une demande chargée).
  const financierName = selectedEnveloppe
    ? selectedEnveloppe.financeurNom
    : financierData.find((f) => f.uuid === financeData.financier)?.name || "—";

  // LS-657/LS-666 — génération de l'accord de financement (BPCE), n'a de sens
  // que si le financeur de la demande a l'option activée.
  const financierEligibleAccord = !!financierData.find(
    (f) =>
      f.uuid === (selectedEnveloppe?.financeurUuid || financeData.financier),
  )?.peutGenererAccordFinancement;

  // LS-657 (addendum) — un devis par ligne (véhicule, quantité, montants) : alimente
  // uniquement l'aperçu de GenererAccordModal, le PDF réel est recalculé côté serveur
  // à partir des mêmes devis (GererEnvoiFinanceur::tousDevisUuids()).
  const accordLignes = financeData.quotations.map((q) => {
    const qObj = getQuotationObj(q.quotation);
    return {
      vehicule: qObj?.vehicle
        ? [qObj.vehicle.brand?.name, qObj.vehicle.brandModel?.name]
            .filter(Boolean)
            .join(" ")
        : qObj?.reference || "",
      quantite: qObj?.quantity || 1,
      montantHtUnitaire: qObj?.vehicle?.investAmtHT || 0,
      // priceNetTTC (prix net après remise/bonus-malus), pas pricePublicTTC (prix catalogue
      // avant négociation) : même champ que le backend (DonneesLigneAccordFinancementEloquentAdapter)
      // et que le tableau récapitulatif (recapData.js).
      montantTtcUnitaire: qObj?.vehicle?.priceNetTTC || 0,
    };
  });

  // Catégories de documents sélectionnables à l'upload manuel : décidées par
  // l'API (enum TypeDocument), pas hardcodées côté front — y compris les
  // règles `requiertUnDevis` / `autoriseMultiDevis` (LS-638). Depuis LS-638,
  // "Calendrier des loyers" (ex-tableau_amortissement_source) redevient
  // sélectionnable ici, au même titre que depuis l'onglet du devis concerné
  // (EcheancierUploader) : les deux points d'entrée coexistent.
  const documentTypeOptions = (
    constantData?.FINANCEMENT?.TYPES_DOCUMENT_UPLOAD_MANUEL || []
  ).map((t) => ({
    value: t.id,
    label: t.name,
    requiertUnDevis: t.requiertUnDevis,
    autoriseMultiDevis: t.autoriseMultiDevis,
  }));

  // LS-638 — devis de la demande, proposés en sélection quand le type de
  // document choisi à l'upload requiert un rattachement à un devis.
  const devisOptions = financeData.quotations.map((q) => ({
    value: q.quotation,
    label: getQuotationObj(q.quotation)?.reference || q.quotation,
  }));

  // LS-638 — types de documents rattachés à un devis (calendrier des loyers,
  // CPI...) : agrégés et affichés dans l'onglet du devis concerné, quel que
  // soit leur point d'upload d'origine (générique ou onglet devis). Depuis
  // ST1-2069 ils figurent aussi dans la section "Documents" générique.
  const devisLinkedTypeOptions = documentTypeOptions.filter(
    (t) => t.requiertUnDevis,
  );

  return (
    <div className={styles.financeDetail}>
      {/* ═══ Header fusionné ═══ */}
      <div className={styles.headerBlock}>
        <div className={styles.headerTop}>
          <div className={styles.headerIdentity}>
            <div className={styles.headerIcon}>F</div>
            <div className={styles.headerTitleGroup}>
              <h1>{isNew ? "Nouveau financement" : "Financement"}</h1>
              {!isNew && (
                <div className={styles.headerSubtitle}>
                  {financeData.reference}
                  {financeData.financeType &&
                    ` · ${financeV2Adapters.FINANCE_TYPE_MAP[financeData.financeType] || financeData.financeType}`}
                </div>
              )}
            </div>
            {!isNew && (
              <span
                className={`${styles.statusBadge} ${
                  STATUS_BADGE_CLASSES[financeData.status] || styles.statusDraft
                }`}
              >
                {statusLabel}
              </span>
            )}
            {!isNew && (
              <span
                className={`${styles.echeancierBadge} ${
                  financeData.echeancierRenseigne
                    ? styles.echeancierOk
                    : styles.echeancierKo
                }`}
                title="Indicateur informatif — n'empêche aucune action"
              >
                {financeData.echeancierRenseigne
                  ? "Échéancier OK"
                  : "Échéancier incomplet"}
              </span>
            )}
            {!isNew && financeData.demandeEnvoyeeLe && (
              <span className={styles.envoyeeLe}>
                Envoyée le {tools.formatDate(financeData.demandeEnvoyeeLe)}
              </span>
            )}
          </div>
          <div className={styles.headerActions}>
            {isCalculating && (
              <span className={styles.calculatingIndicator}>
                <i className="fa fa-spinner fa-spin" />
                Calcul...
              </span>
            )}
            {!isReadOnly && (
              <>
                {isLoading ? (
                  <Loader />
                ) : (
                  <button
                    className={styles.btnSave}
                    onClick={handleSave}
                    disabled={isCalculating}
                  >
                    <i
                      className={`fa fa-${isNew ? "plus" : "save"}`}
                      style={{ marginRight: 4 }}
                    />
                    {isNew ? "Créer" : "Enregistrer"}
                  </button>
                )}
                {financeData.uuid && nextTransition && financeData.status <= 3 && (
                  <button
                    className={styles.btnValidate}
                    onClick={handleTransition}
                    disabled={isLoading}
                  >
                    <i
                      className={`fa fa-${nextTransition.icon}`}
                      style={{ marginRight: 4 }}
                    />
                    {nextTransition.label}
                  </button>
                )}
              </>
            )}
            {/* LS-637 — les transitions au-delà de Dossier complet (dossier
                transmis, transmis et signé) sont réservées aux gestionnaires. */}
            {financeData.uuid &&
              nextTransition &&
              financeData.status >= 4 &&
              isGestionnaire && (
                <button
                  className={styles.btnConfirm}
                  onClick={handleTransition}
                  disabled={isLoading}
                >
                  <i
                    className={`fa fa-${nextTransition.icon}`}
                    style={{ marginRight: 4 }}
                  />
                  {nextTransition.label}
                </button>
              )}
            {/* LS-625 — envoi (ou renvoi) par email au financeur, distinct des
                transitions de statut ci-dessus. N'a de sens que tant que le
                financeur n'a pas encore répondu (Brouillon/Demande envoyée) —
                au-delà, même un gestionnaire (jamais verrouillé par ailleurs)
                ne peut plus (re)envoyer, cf. StatutDemande::estEnvoyableAuFinanceur(). */}
            {financeData.uuid && !isReadOnly && financeData.status <= 2 && (
              <button
                className={styles.btnEnvoyerFinanceur}
                onClick={() => setIsEnvoyerAuFinanceurOpen(true)}
                disabled={isLoading}
              >
                <i className="fa fa-envelope" style={{ marginRight: 4 }} />
                {envoyerAuFinanceurLabel}
              </button>
            )}
            {/* LS-629 — réservé au back-office, depuis n'importe quel statut. */}
            {financeData.uuid && isGestionnaire && !isAbandonnee && (
              <button
                className={styles.btnAbandonner}
                onClick={handleAbandonner}
                disabled={isLoading}
              >
                <i className="fa fa-ban" style={{ marginRight: 4 }} />
                Abandonner la demande
              </button>
            )}
          </div>
        </div>

        {!isNew && (
          <div className={styles.lifecycle}>
            {LIFECYCLE_STEPS.map((step, i) => {
              // LS-633 — date de passage de l'étape, absente si non franchie
              // ou désactivée sur l'enveloppe (cf. LS-632).
              const etape = financeData.etapier.find(
                (e) => e.statut === step.status,
              );
              return (
                <React.Fragment key={step.status}>
                  {i > 0 && <span className={styles.lifecycleSep}>→</span>}
                  <span
                    className={
                      financeData.status === step.status
                        ? styles.lifecycleOn
                        : ""
                    }
                  >
                    <span className={styles.lifecycleLabel}>
                      {financeData.status > step.status
                        ? "✓ "
                        : financeData.status === step.status
                          ? "● "
                          : ""}
                      {step.label}
                    </span>
                    {etape && (
                      <span className={styles.lifecycleDate}>
                        {tools.formatDate(etape.date)}
                      </span>
                    )}
                  </span>
                </React.Fragment>
              );
            })}
          </div>
        )}

        {/* KPI Strip */}
        <div className={styles.kpiStrip}>
          <div className={styles.kpiCell}>
            <div className={styles.kpiLabel}>Investissement</div>
            <div className={styles.kpiValue}>
              {financeData.totalEnvelop.toLocaleString("fr-FR")} €
            </div>
          </div>
          <div className={styles.kpiCell}>
            <div className={styles.kpiLabel}>Enveloppe dispo.</div>
            {envConso && enveloppeOrigine > 0 ? (
              <div className={styles.kpiEnveloppeRow}>
                <div
                  className={styles.kpiValue}
                  style={{ color: enveloppeColorText }}
                >
                  {Number(enveloppeDispo).toLocaleString("fr-FR")} €
                </div>
                <div className={styles.kpiMiniBar}>
                  <div
                    className={styles.kpiMiniBarFill}
                    style={{
                      width: `${enveloppeDispoRatio}%`,
                      backgroundColor: enveloppeColor,
                    }}
                  />
                </div>
              </div>
            ) : (
              <div className={styles.kpiValueMuted}>—</div>
            )}
          </div>
          <div className={styles.kpiCell}>
            <div className={styles.kpiLabel}>Devis</div>
            <div className={styles.kpiValue}>
              {financeData.quotations.length}
            </div>
          </div>
          <div className={styles.kpiCell}>
            <div className={styles.kpiLabel}>Financeur</div>
            <div className={styles.kpiFinancier}>{financierName}</div>
          </div>
          {/* LS-716 — saisie dès Accord reçu (même seuil que l'échéancier), y
              compris pour un commercial ; masquée tant qu'il n'y a ni numéro
              ni droit de saisie. */}
          {!isNew && financeData.uuid && (
            <NumeroContratFinanceur
              demandeUuid={financeData.uuid}
              numeroContrat={financeData.numeroContratFinanceur}
              canEdit={canSaisirNumeroContrat}
              onSaved={() => loadDemande(financeData.uuid)}
            />
          )}
          {/* LS-717 — compte comptable du dossier, même seuil de saisie que
              le n° de contrat financeur. */}
          {!isNew && financeData.uuid && (
            <CompteComptable
              demandeUuid={financeData.uuid}
              compteComptable={financeData.compteComptable}
              canEdit={canSaisirNumeroContrat}
              onSaved={() => loadDemande(financeData.uuid)}
            />
          )}
        </div>
      </div>

      {/* ═══ Section 1 : Configuration ═══ */}
      {/* Sur une demande déjà enregistrée : replié pour gagner de la place, avec
          rappel financeur + enveloppe à côté du titre. */}
      <AccordionSection
        title="Configuration"
        icon="cog"
        accent="red"
        defaultOpen={isNew}
        headerExtra={
          !isNew && selectedEnveloppe ? (
            <>
              <i className="fa fa-university" style={{ marginRight: 4 }} />
              {financierName}
              <span className={styles.accordionHeaderExtraSep}>·</span>
              {selectedEnveloppe.nom}
            </>
          ) : null
        }
      >
        <div className={styles.financierBlock}>
          <label className={styles.searchLabel}>Financeur &amp; enveloppe</label>
          <EnveloppeSelector
            groupes={groupes}
            selectedEnveloppeUuid={financeData.enveloppe}
            totalEnvelop={financeData.totalEnvelop}
            derniereEcheance={derniereEcheance}
            enveloppeDejaImputee={
              !isNew && financeData.uuid ? financeData.enveloppe : null
            }
            disabled={isReadOnly}
            onSelect={changeEnveloppe}
          />
        </div>
      </AccordionSection>

      {/* ═══ Checklist + Documents, côte à côte (LS-646) : rapprochées pour
          vérifier que les documents attendus par la checklist ont bien été
          envoyés, sans avoir à scroller entre les deux sections. Empilées
          verticalement (Checklist puis Documents) sous 768px. ═══ */}
      <div className={styles.checklistDocumentsRow}>
        {/* Toujours modifiable, y compris demande verrouillée (isReadOnly) et sur
            une étape déjà dépassée — les todos sont purement informatifs et
            indépendants de estModifiable(). Les étapes de statut n'étant jamais
            rejouables, cette vue est le seul moyen de revoir/corriger ce qui a
            été fait à une étape passée. */}
        {!isNew && financeData.todos.length > 0 && (
          <AccordionSection
            title="Checklist"
            icon="tasks"
            accent="green"
            badge={
              financeData.todos.filter((t) => t.coche).length +
              "/" +
              financeData.todos.length
            }
          >
            {LIFECYCLE_STEPS.map((step) => {
              const todosEtape = financeData.todos
                .filter((t) => t.statut === step.status)
                .sort((a, b) => a.ordre - b.ordre);
              if (todosEtape.length === 0) return null;
              const isCurrent = step.status === financeData.status;
              const isOpen = expandedSteps.has(step.status);
              const doneCount = todosEtape.filter((t) => t.coche).length;
              return (
                <div
                  key={step.status}
                  className={`${styles.checklistStep} ${isCurrent ? styles.checklistStepCurrent : ""}`}
                >
                  <button
                    type="button"
                    className={styles.checklistStepHeader}
                    onClick={() => toggleStep(step.status)}
                    aria-expanded={isOpen}
                  >
                    <i
                      className={`fa fa-chevron-${isOpen ? "down" : "right"}`}
                    />
                    <span className={styles.checklistStepLabel}>
                      {step.label}
                      {isCurrent && (
                        <span className={styles.checklistStepCurrentTag}>
                          étape en cours
                        </span>
                      )}
                    </span>
                    <span className={styles.checklistStepCount}>
                      {doneCount}/{todosEtape.length}
                    </span>
                  </button>
                  {isOpen && (
                    <ul className={styles.checklist}>
                      {todosEtape.map((todo) => (
                        <li key={todo.uuid} className="form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id={"todo-" + todo.uuid}
                            checked={todo.coche}
                            // LS-629 — seul cas où cocher un todo est bloqué, y compris
                            // pour un gestionnaire.
                            disabled={isAbandonnee}
                            onChange={(e) =>
                              basculerTodo(todo.uuid, e.target.checked)
                            }
                          />
                          <label
                            className="form-check-label"
                            htmlFor={"todo-" + todo.uuid}
                          >
                            {todo.libelle}
                            {todo.coche && todo.validePar && (
                              <span className={styles.checklistMeta}>
                                {" "}
                                — {todo.validePar}
                                {todo.valideLe
                                  ? ` le ${tools.formatDate(todo.valideLe)}`
                                  : ""}
                              </span>
                            )}
                          </label>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </AccordionSection>
        )}

        {!isNew && financeData.uuid && (
          <AccordionSection title="Documents" icon="paperclip" accent="gray">
            <DocumentsUploader
              uuid={financeData.uuid}
              // LS-637 — toujours autorisé, quel que soit le statut, sauf abandon (LS-629,
              // seul cas où même un gestionnaire est bloqué).
              disabled={isAbandonnee}
              // LS-649 — suppression bloquée pour un commercial dès Dossier
              // complet, ou pour tout le monde si la demande est abandonnée (isReadOnly).
              canDelete={!isReadOnly}
              typeOptions={documentTypeOptions}
              devisOptions={devisOptions}
              reloadKey={documentsVersion}
              // ST1-2069 — l'onglet du devis concerné recharge sa liste ; seul le
              // calendrier des loyers pèse sur l'indicateur « Échéancier OK »
              // (LS-634), d'où le rechargement de la demande dans ce seul cas.
              onChanged={({ type }) => {
                setDevisDocumentsVersion((v) => v + 1);
                if (type === TYPE_TABLEAU_AMORTISSEMENT_SOURCE) {
                  loadDemande(financeData.uuid);
                }
              }}
            />
          </AccordionSection>
        )}

        {/* LS-657/LS-666/LS-668 — masquée si le financeur n'a pas l'option
            "génération d'accord" activée (cf. StatutDemande::estEligibleGenerationAccord()
            pour le seuil de statut, vérifié dans le composant lui-même). */}
        {!isNew && financeData.uuid && financierEligibleAccord && (
          <AccordionSection
            title="Accord de financement (BPCE)"
            icon="file-pdf-o"
            accent="gray"
          >
            <AccordFinancementSection
              demandeUuid={financeData.uuid}
              statutEligible={financeData.status >= 2}
              disabled={isAbandonnee}
              lignes={accordLignes}
              expediteurNom={tools.getFullname(state.auth.user)}
              expediteurSignatureUrl={state.auth.user.signatureUrl}
              tampons={state.auth.user.tampons || []}
              fournisseurUuid={financeData.accordFournisseurGenereUuid}
              accordEnvoyeFournisseur={financeData.accordEnvoyeFournisseur}
              onEnvoye={() => loadDemande(financeData.uuid)}
            />
          </AccordionSection>
        )}
      </div>

      {/* ═══ Soldes anticipés (LS-722) — suivi manuel par véhicule, ouvert dès
          Accord reçu (même seuil que l'API : estAuMoinsAccordRecu). ═══ */}
      {!isNew && financeData.uuid && financeData.status >= 3 && (
        <AccordionSection title="Soldes anticipés" icon="money" accent="gray">
          <SoldesSection
            demandeUuid={financeData.uuid}
            disabled={isAbandonnee}
          />
        </AccordionSection>
      )}

      {/* ═══ Section 2 : Sélection des devis (masquée une fois validé) ═══ */}
      {!isReadOnly && (
        <AccordionSection
          title="Sélection des devis"
          icon="search"
          accent="orange"
          badge={financeData.quotations.length || null}
          defaultOpen={isNew}
        >
          <DevisSearchV2
            quotationData={quotationData}
            selectedQuotations={financeData.quotations}
            disabled={isReadOnly}
            isLoading={isSearchingQuotations}
            defaultReference={embedded ? props.reference : ""}
            onSelect={selectQuotation}
            onUnselect={unselectQuotation}
            onSearch={onSearchValidQuotations}
          />
        </AccordionSection>
      )}

      {/* ═══ Section 3 : Périodes par devis ═══ */}
      {financeData.quotations.length > 0 && (
        <AccordionSection
          title="Périodes par devis"
          icon="calendar-alt"
          accent="blue"
        >
          {/* Liste accordéon : une ligne dépliable par devis (un seul ouvert). */}
          <div className={styles.devisAccordion}>
            {financeData.quotations.map((q, k) => {
              const qObj = getQuotationObj(q.quotation);
              // LS-693 (suite) — lien vers le dossier parent, hors modale
              // uniquement : la modale est ouverte depuis ce dossier même,
              // le lien serait circulaire.
              const dossier = embedded
                ? null
                : financeV2Adapters.trouverDossierDuDevis(
                    [...demandeQuotations, ...quotationData],
                    q.quotation,
                  );
              const isOpen = k === selectedTab;
              const totalDuration = q.periods.reduce(
                (sum, p) => sum + (Number(p.duration) || 0),
                0,
              );
              const toggle = () => setSelectedTab(isOpen ? -1 : k);
              return (
                <div key={"dacc-" + k} className={styles.devisAccItem}>
                  <div
                    className={`${styles.devisAccHeader} ${isOpen ? styles.devisAccHeaderOpen : ""}`}
                    role="button"
                    tabIndex={0}
                    aria-expanded={isOpen}
                    onClick={toggle}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        toggle();
                      }
                    }}
                  >
                    <i
                      className={`fa fa-chevron-${isOpen ? "down" : "right"} ${styles.devisAccChevron}`}
                    />
                    <span className={styles.devisAccName}>
                      {qObj ? (
                        <>
                          {qObj.reference}
                          {qObj.quantity > 1 && ` x${qObj.quantity}`}
                          <FuelBadge quotationObj={qObj} />
                        </>
                      ) : (
                        "Devis introuvable"
                      )}
                      {dossier && (
                        /* Nouvel onglet : la demande en cours d'édition reste
                           en place. stopPropagation (clic et clavier) : l'en-tête
                           qui l'entoure est le bouton d'accordéon. */
                        <a
                          href={`/quotations/${dossier.uuid}`}
                          target="_blank"
                          rel="noreferrer"
                          className={styles.devisAccDossier}
                          title={`Ouvrir le dossier ${dossier.reference}`}
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => e.stopPropagation()}
                        >
                          <i
                            className="fa fa-external-link"
                            style={{ marginRight: 4 }}
                          />
                          Dossier
                        </a>
                      )}
                    </span>
                    <span className={styles.devisAccMeta}>
                      {qObj?.vehicle?.investAmtHT
                        ? `${Number(qObj.vehicle.investAmtHT).toLocaleString("fr-FR")} € HT · `
                        : ""}
                      {totalDuration} mois · {q.periods.length} période
                      {q.periods.length > 1 ? "s" : ""}
                    </span>
                    {!isReadOnly && (
                      <button
                        className={styles.devisAccRemove}
                        onClick={(e) => {
                          e.stopPropagation();
                          unselectQuotation(q.quotation);
                        }}
                        title="Retirer ce devis"
                      >
                        <i className="fa fa-times" />
                      </button>
                    )}
                  </div>
                  {isOpen && (
                    <div className={styles.devisAccBody}>
                      <FinanceTabV2
                        quotation={q}
                        quotationIndex={k}
                        isCalculating={isCalculating}
                        disabled={isReadOnly}
                        canSaisirEcheancier={canSaisirEcheancier}
                        savedPeriodsCount={savedPeriodsCount[q.quotation]}
                        onFieldChange={updatePeriodField}
                        onFieldCommit={commitPeriodField}
                        onAddPeriod={addPeriod}
                        onRemovePeriod={removePeriod}
                        onSaisirMensualiteRetenue={(numero, payload) =>
                          saisirMensualiteRetenue(k, numero, payload)
                        }
                        demandeUuid={financeData.uuid}
                        // LS-637 — l'upload de documents reste toujours autorisé,
                        // pour tous les rôles pouvant accéder à cette page, quel
                        // que soit le statut de la demande.
                        documentUploadDisabled={false}
                        onDocumentUploaded={() => {
                          // ST1-2069 — la section "Documents" liste aussi ce dépôt.
                          setDocumentsVersion((v) => v + 1);
                          loadDemande(financeData.uuid);
                        }}
                        documentsReloadKey={devisDocumentsVersion}
                        // LS-638 — tous les devis de la demande, pour permettre de
                        // rattacher le même fichier à plusieurs d'entre eux.
                        devisOptions={devisOptions}
                        devisLinkedTypeOptions={devisLinkedTypeOptions}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </AccordionSection>
      )}

      {/* ═══ Section 4 : Tableau récapitulatif ═══ */}
      <AccordionSection
        title="Tableau récapitulatif"
        icon="table"
        accent="gray"
        defaultOpen={!isNew}
      >
        {!financeData.uuid && (
          <div className={styles.printGate}>
            <span>
              <i className="fa fa-info-circle" />
              Enregistrez la demande pour pouvoir télécharger le tableau
              récapitulatif.
            </span>
            <button
              className={styles.btnSave}
              onClick={handleSave}
              disabled={isLoading || isCalculating}
            >
              <i className="fa fa-save" style={{ marginRight: 4 }} />
              Enregistrer la demande
            </button>
          </div>
        )}
        {/* LS-625 — rappel du dernier envoi au financeur, pour éviter un renvoi
            par inadvertance à la mauvaise adresse. */}
        {dejaEnvoyeAuFinanceur && (
          <div className={styles.recapEnvoyeInfo}>
            <i className="fa fa-check-circle" style={{ marginRight: 6 }} />
            Déjà envoyé au financeur — dernier destinataire :{" "}
            {financeData.dernierEnvoiFinanceur.destinataire}
            {financeData.dernierEnvoiFinanceur.cc?.length > 0 &&
              ` (cc : ${financeData.dernierEnvoiFinanceur.cc.join(", ")})`}
          </div>
        )}
        <div className={styles.paymentTableWrapper}>
          <PaymentTable
            quotations={[...demandeQuotations, ...quotationData]}
            finances={financeData}
            showPrintButton={!!financeData.uuid}
          />
        </div>
      </AccordionSection>

      {/* ═══ Barre d'action sticky : actions toujours atteignables ═══ */}
      {/* LS-637 — un commercial verrouillé (statut > Dossier complet) n'a plus
          aucune action possible : ne pas afficher de barre vide dans ce cas. */}
      {(!isReadOnly ||
        (financeData.uuid &&
          nextTransition &&
          financeData.status >= 4 &&
          isGestionnaire)) && (
        <div className={styles.stickyActionBar}>
          <div className={styles.stickyStatus}>
            {!isReadOnly &&
              (isDirty ? (
                <span className={styles.stickyDirty}>
                  <i className="fa fa-circle" />
                  {isNew
                    ? "Demande non enregistrée"
                    : "Modifications non enregistrées"}
                </span>
              ) : (
                <span className={styles.stickyClean}>
                  <i className="fa fa-check-circle" />
                  Tout est enregistré
                </span>
              ))}
          </div>
          <div className={styles.stickyActions}>
            {!isReadOnly &&
              (isLoading ? (
                <Loader />
              ) : (
                <button
                  className={styles.btnSave}
                  onClick={handleSave}
                  disabled={isCalculating}
                >
                  <i
                    className={`fa fa-${isNew ? "plus" : "save"}`}
                    style={{ marginRight: 4 }}
                  />
                  {isNew ? "Créer" : "Enregistrer"}
                </button>
              ))}
            {!isReadOnly &&
              financeData.uuid &&
              nextTransition &&
              financeData.status <= 3 && (
                <button
                  className={styles.btnValidate}
                  onClick={handleTransition}
                  disabled={isLoading}
                >
                  <i
                    className={`fa fa-${nextTransition.icon}`}
                    style={{ marginRight: 4 }}
                  />
                  {nextTransition.label}
                </button>
              )}
            {/* LS-637 — les transitions au-delà de Dossier complet (dossier
                transmis, transmis et signé) sont réservées aux gestionnaires. */}
            {financeData.uuid &&
              nextTransition &&
              financeData.status >= 4 &&
              isGestionnaire && (
                <button
                  className={styles.btnConfirm}
                  onClick={handleTransition}
                  disabled={isLoading}
                >
                  <i
                    className={`fa fa-${nextTransition.icon}`}
                    style={{ marginRight: 4 }}
                  />
                  {nextTransition.label}
                </button>
              )}
            {financeData.uuid && !isReadOnly && financeData.status <= 2 && (
              <button
                className={styles.btnEnvoyerFinanceur}
                onClick={() => setIsEnvoyerAuFinanceurOpen(true)}
                disabled={isLoading}
              >
                <i className="fa fa-envelope" style={{ marginRight: 4 }} />
                {envoyerAuFinanceurLabel}
              </button>
            )}
            {/* LS-629 — réservé au back-office, depuis n'importe quel statut. */}
            {financeData.uuid && isGestionnaire && !isAbandonnee && (
              <button
                className={styles.btnAbandonner}
                onClick={handleAbandonner}
                disabled={isLoading}
              >
                <i className="fa fa-ban" style={{ marginRight: 4 }} />
                Abandonner la demande
              </button>
            )}
          </div>
        </div>
      )}

      {!embedded && (
        <Prompt
          when={isDirty}
          message="Vous avez des modifications non enregistrées. Quitter cette page sans enregistrer ?"
        />
      )}

      <ConfirmModal
        isOpen={confirmState.isOpen}
        message={confirmState.message}
        items={confirmState.items}
        itemsLabel={
          confirmState.items.length > 0
            ? "Todos à faire à la prochaine étape :"
            : null
        }
        isLoading={isLoading}
        onConfirm={confirmTransition}
        onCancel={cancelTransition}
      />

      <TauxVrManuelModal
        isOpen={!!tauxVrManuelPrompt}
        message={tauxVrManuelPrompt?.message}
        isLoading={isCalculating}
        onSubmit={submitTauxVrManuel}
        onCancel={cancelTauxVrManuel}
      />

      <EnvoyerAuFinanceurModal
        isOpen={isEnvoyerAuFinanceurOpen}
        financeurUuid={financeData.financier}
        dernierEnvoi={financeData.dernierEnvoiFinanceur}
        expediteurSignatureUrl={state.auth.user.signatureUrl}
        onSubmit={handleEnvoyerAuFinanceur}
        onClose={() => setIsEnvoyerAuFinanceurOpen(false)}
      />
    </div>
  );
};

export default withRouter(FinanceDetailV2);
